import { axios } from '@getgreenline/homi-shared'
import { CompaniesModels } from '@getgreenline/companies'

export interface IOrganizationWithCompanies {
  id: number
  name: string
  companies: Pick<CompaniesModels.ICompany, 'id' | 'name' | 'province'>[]
}

export const getOrganizationWithCompanies = (
  organizationId: number,
): Promise<IOrganizationWithCompanies> =>
  axios.get(`/api/v1/admin/organizations/${organizationId}`).then((r) => r.data)

export const removeCompanyFromOrganization = (
  organizationId: number,
  companyId: number,
): Promise<void> =>
  axios
    .delete(`/api/v1/admin/organizations/${organizationId}/migrations/${companyId}`)
    .then(() => undefined)

export interface IOrgProductSyncStarted {
  organizationId: number
  companyCount: number
  productCount: number
}

/**
 * Kicks off a re-emit of product.updated for every product already in the organization's product
 * groups, so product-service can re-ingest them into OpenSearch. Returns 202 immediately with the
 * counts the run is expected to cover; the sync itself continues server-side.
 */
export const syncOrganizationProducts = (organizationId: number): Promise<IOrgProductSyncStarted> =>
  axios.post(`/api/v1/admin/organizations/${organizationId}/sync-products`).then((r) => r.data)

// The org-products purge is served by product-service (OpenSearch), reached through the platform
// gateway with the same auth token as homi-api.
const PRODUCT_SERVICE_BASE_URL = process.env.REACT_APP_PLATFORM_URL

/**
 * Starts a purge of a company's documents from the `org-products` OpenSearch index, after the
 * company has been detached from the organization (its `products.orgProductGroupId` has already
 * been cleared in MySQL).
 *
 * Fire-and-forget: the 200 carries the `executionArn` of the state machine doing the work, so a
 * resolved promise only means the purge started, not that it finished. Nothing polls it, the same
 * way `syncOrganizationProducts` above is treated. Idempotent — safe to start again; a company
 * with nothing left to purge is a no-op.
 */
export const purgeOrgProductsForCompany = (
  organizationId: number,
  companyId: number,
): Promise<void> =>
  axios
    .delete(
      `${PRODUCT_SERVICE_BASE_URL}/product-service/api/v1/organizations/${organizationId}/companies/${companyId}/products`,
    )
    .then(() => undefined)
